/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { User } from "oidc-client-ts";
import { useAuthSwitcherStore, deriveNameFromEmail } from "@/features/dev-cockpit/services/auth-switcher";

const DEFAULT_EMAIL = 'lucas@k4d.com.br';

/**
 * Resolves the email to use for the mock user, reading from the
 * dev-cockpit auth switcher store with a sensible default.
 */
export function getImpersonatedEmail(): string {
  return useAuthSwitcherStore.getState().getActiveEmail() ?? DEFAULT_EMAIL;
}

/**
 * Resolves the display name claims for the mock user. Prefers the active
 * impersonation profile's given/family names; otherwise derives them from the
 * email local-part so the greeting and header always show a real first name.
 *
 * @param email - The resolved impersonation email.
 * @returns The given and family names for SSO claim emulation.
 */
export function resolveMockName(email: string): { givenName: string; familyName: string } {
  const profile = useAuthSwitcherStore.getState().getActiveProfile();
  if (profile?.givenName) {
    return { givenName: profile.givenName, familyName: profile.familyName };
  }
  return deriveNameFromEmail(email);
}

/**
 * Creates a mock user object that matches the User type from oidc-client-ts,
 * mirroring the full claim set (name, given_name, family_name, full_name) that
 * real Google/K4D SSO provides so name-dependent UI behaves identically.
 *
 * @returns {User} A mock user with impersonated credentials and name claims.
 */
export const createMockUser = (): User => {
  const now = Math.floor(Date.now() / 1000);
  const expiresAt = now + 3600; // Token expires in 1 hour
  const email = getImpersonatedEmail();
  const { givenName, familyName } = resolveMockName(email);
  const fullName = `${givenName} ${familyName}`.trim();

  return {
    id_token: "mock-id-token",
    access_token: "mock-access-token",
    refresh_token: "mock-refresh-token",
    token_type: "Bearer",
    scope: "openid",
    profile: {
      sub: `mock-${email}`,
      email,
      email_verified: true,
      name: fullName,
      given_name: givenName,
      family_name: familyName,
      full_name: fullName,
      preferred_username: email,
      idp: 'google',
    },
    expires_at: expiresAt,
    state: undefined,
    session_state: "mock-session-state",
  } as unknown as User;
};

// Mock auth context interface matching react-oidc-context
export interface MockAuthContextType {
  user: User | null | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null | undefined;
  signin: () => Promise<void>;
  signout: () => Promise<void>;
  removeUser: () => Promise<void>;
  signinSilent: () => Promise<User | null>;
  navigateToProtectedRoute: (path: string, navigate: (path: string) => void) => void;
}

/**
 * Fallback auth state returned when the mock provider is not yet available (e.g. during HMR).
 */
export const FALLBACK_AUTH: MockAuthContextType = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
  signin: async () => {},
  signout: async () => {},
  removeUser: async () => {},
  signinSilent: async () => null,
  navigateToProtectedRoute: () => {},
};
